/**
 * Gestão de templates de projeto (G3 da jornada do Inovação).
 * O backend já tem o CRUD completo (criar com fases, editar meta, duplicar,
 * desativar). Esta tela é a UI que faltava — a equipe define os modelos padrão
 * de fases & entregáveis pela tela, em vez de só por seed no banco.
 *
 * v1: listar, criar (meta + fases), duplicar, desativar. Editar as fases de um
 * template existente: use "duplicar" e recrie (o backend só aceita fases no create).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Plus, Copy, Trash2, ChevronDown, ChevronRight, ListTree, X,
} from 'lucide-react';
import { templatesAPI } from '../../services/inovacaoAPI';
import { mensagemErro } from './labels';

const BRAND = 'var(--brand, #20a490)';
const inp = { width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '0.88rem' };

export default function TemplatesProjeto() {
  const [templates, setTemplates] = useState([]);
  const [expandido, setExpandido] = useState(null);
  const [criando, setCriando] = useState(false);
  const [loading, setLoading] = useState(true);

  async function carregar() {
    try { setTemplates(await templatesAPI.listar()); }
    catch (e) { toast.error(mensagemErro(e, 'Erro ao carregar templates')); }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function duplicar(id) {
    try { await templatesAPI.duplicar(id); toast.success('Template duplicado'); carregar(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro ao duplicar')); }
  }
  async function desativar(id) {
    if (!window.confirm('Desativar este template? Ele some da lista de criação de projeto.')) return;
    try { await templatesAPI.desativar(id); toast.success('Template desativado'); carregar(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro ao desativar')); }
  }

  if (loading) return <div style={{ padding: 40 }}>Carregando…</div>;

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      <Link to="/inovacao" style={{ color: '#7f8c8d', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
        <ArrowLeft size={16} /> Gestão de Projetos
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <ListTree size={24} style={{ color: BRAND }} /> Templates de projeto
        </h1>
        {!criando && (
          <button onClick={() => setCriando(true)} style={btn(BRAND)}>
            <Plus size={16} /> Novo template
          </button>
        )}
      </div>
      <p style={{ color: '#7f8c8d', marginTop: 0, marginBottom: 24 }}>
        Modelos de fases & entregáveis. Ao criar um projeto, escolher um template gera as fases automaticamente.
      </p>

      {criando && <FormCriar onCancelar={() => setCriando(false)} onCriado={() => { setCriando(false); carregar(); }} />}

      <div style={{ display: 'grid', gap: 12 }}>
        {templates.length === 0 && !criando && (
          <div style={card}><p style={{ color: '#bdc3c7', margin: 0 }}>Nenhum template ativo. Crie o primeiro.</p></div>
        )}
        {templates.map((t) => {
          const aberto = expandido === t.id;
          const totalH = (t.fases || []).reduce((s, f) => s + parseFloat(f.horas_estimadas_padrao || 0), 0);
          return (
            <div key={t.id} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setExpandido(aberto ? null : t.id)} style={iconBtn}>
                  {aberto ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#2c3e50' }}>{t.nome}</div>
                  <div style={{ fontSize: '0.82rem', color: '#7f8c8d' }}>
                    {t.categoria ? `${t.categoria} · ` : ''}{(t.fases || []).length} fase(s) · {totalH}h · buffer {t.buffer_percentual_sugerido}%
                  </div>
                </div>
                <button onClick={() => duplicar(t.id)} title="Duplicar" style={iconBtn}><Copy size={16} /></button>
                <button onClick={() => desativar(t.id)} title="Desativar" style={{ ...iconBtn, color: '#e74c3c' }}><Trash2 size={16} /></button>
              </div>
              {aberto && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
                  <thead><tr>
                    <th style={th}>#</th><th style={th}>Fase / entregável</th><th style={th}>Categoria</th><th style={th}>Horas</th>
                  </tr></thead>
                  <tbody>
                    {(t.fases || []).sort((a, b) => a.ordem - b.ordem).map((f) => (
                      <tr key={f.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={td}>{f.ordem}</td>
                        <td style={td}>{f.eh_buffer && '🔸 '}{f.nome}</td>
                        <td style={td}>{f.categoria || '—'}</td>
                        <td style={td}>{parseFloat(f.horas_estimadas_padrao || 0)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FormCriar({ onCancelar, onCriado }) {
  const [meta, setMeta] = useState({ nome: '', categoria: '', descricao: '', buffer_percentual_sugerido: 15 });
  const [fases, setFases] = useState([{ nome: '', categoria: '', horas_estimadas_padrao: '', eh_buffer: false }]);
  const [salvando, setSalvando] = useState(false);

  const setFase = (i, campo, val) => setFases((fs) => fs.map((f, idx) => idx === i ? { ...f, [campo]: val } : f));
  const addFase = () => setFases((fs) => [...fs, { nome: '', categoria: '', horas_estimadas_padrao: '', eh_buffer: false }]);
  const rmFase = (i) => setFases((fs) => fs.filter((_, idx) => idx !== i));

  async function salvar(e) {
    e.preventDefault();
    if (!meta.nome.trim()) { toast.error('Nome do template é obrigatório'); return; }
    const fasesValidas = fases.filter((f) => f.nome.trim());
    if (fasesValidas.length === 0) { toast.error('Adicione ao menos uma fase'); return; }
    setSalvando(true);
    try {
      await templatesAPI.criar({
        nome: meta.nome.trim(),
        categoria: meta.categoria.trim() || null,
        descricao: meta.descricao.trim() || null,
        buffer_percentual_sugerido: parseInt(meta.buffer_percentual_sugerido) || 0,
        fases: fasesValidas.map((f, idx) => ({
          ordem: idx + 1,
          nome: f.nome.trim(),
          categoria: f.categoria.trim() || null,
          horas_estimadas_padrao: parseFloat(f.horas_estimadas_padrao) || 0,
          eh_buffer: !!f.eh_buffer,
        })),
        dependencias: [],
      });
      toast.success('Template criado');
      onCriado();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao criar template'));
    } finally { setSalvando(false); }
  }

  return (
    <form onSubmit={salvar} style={{ ...card, marginBottom: 16, border: `1px solid ${BRAND}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>Novo template</h3>
        <button type="button" onClick={onCancelar} style={iconBtn}><X size={18} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>Nome *</label><input style={inp} value={meta.nome} onChange={(e) => setMeta({ ...meta, nome: e.target.value })} /></div>
        <div><label style={lbl}>Categoria</label><input style={inp} value={meta.categoria} onChange={(e) => setMeta({ ...meta, categoria: e.target.value })} /></div>
        <div><label style={lbl}>Buffer (%)</label><input type="number" min="0" max="50" style={inp} value={meta.buffer_percentual_sugerido} onChange={(e) => setMeta({ ...meta, buffer_percentual_sugerido: e.target.value })} /></div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Descrição</label>
        <textarea style={{ ...inp, minHeight: 50 }} value={meta.descricao} onChange={(e) => setMeta({ ...meta, descricao: e.target.value })} />
      </div>

      <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: 8 }}>Fases & entregáveis</div>
      {fases.map((f, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 2fr 1fr 90px 90px 28px', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span style={{ color: '#95a5a6', textAlign: 'center' }}>{i + 1}</span>
          <input placeholder="Nome da fase / entregável" style={inp} value={f.nome} onChange={(e) => setFase(i, 'nome', e.target.value)} />
          <input placeholder="Categoria" style={inp} value={f.categoria} onChange={(e) => setFase(i, 'categoria', e.target.value)} />
          <input type="number" step="0.5" min="0" placeholder="Horas" style={inp} value={f.horas_estimadas_padrao} onChange={(e) => setFase(i, 'horas_estimadas_padrao', e.target.value)} />
          <label style={{ fontSize: '0.78rem', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.eh_buffer} onChange={(e) => setFase(i, 'eh_buffer', e.target.checked)} /> buffer
          </label>
          <button type="button" onClick={() => rmFase(i)} style={{ ...iconBtn, color: '#e74c3c' }} disabled={fases.length === 1}><X size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={addFase} style={{ ...btn('#ecf0f1'), color: '#555', marginTop: 4 }}>
        <Plus size={14} /> Adicionar fase
      </button>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button type="submit" disabled={salvando} style={btn(BRAND)}>{salvando ? 'Salvando…' : 'Criar template'}</button>
        <button type="button" onClick={onCancelar} style={{ ...btn('#ecf0f1'), color: '#555' }}>Cancelar</button>
      </div>
    </form>
  );
}

const card = { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
const btn = (bg) => ({ background: bg, color: '#fff', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 });
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#7f8c8d', display: 'inline-flex', alignItems: 'center', padding: 4 };
const lbl = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.82rem', color: '#555' };
const th = { textAlign: 'left', padding: '6px 8px', fontSize: '0.72rem', color: '#95a5a6', textTransform: 'uppercase' };
const td = { padding: '6px 8px', fontSize: '0.85rem', color: '#2c3e50' };
